/**
 * EPC QR Code payload for SEPA Credit Transfer (European Payments Council).
 * Scannable by many Belgian/EU banking apps.
 *
 * Spec: EPC069-12 (BCD / SCT). Belgian OGM-VCS goes in structured remittance
 * as 12 digits (no +++ or slashes).
 *
 * @param {{
 *   beneficiaryName: string,
 *   iban: string,
 *   amount: number|string,
 *   bic?: string|null,
 *   structuredMededeling?: string|null,
 *   unstructuredRemittance?: string|null,
 * }} params
 * @returns {string} LF-separated EPC payload
 */
const generateEpcQrPayload = ({
  beneficiaryName,
  iban,
  amount,
  bic = "",
  structuredMededeling = null,
  unstructuredRemittance = null,
}) => {
  const name = String(beneficiaryName ?? "").trim().slice(0, 70);
  const cleanIban = String(iban ?? "").replace(/\s/g, "").toUpperCase();
  const cleanBic = String(bic ?? "").replace(/\s/g, "").toUpperCase();

  if (!name) {
    throw new Error("beneficiaryName is required for EPC QR");
  }
  if (!cleanIban) {
    throw new Error("iban is required for EPC QR");
  }

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount < 0.01) {
    throw new Error("amount must be at least 0.01 EUR for EPC QR");
  }
  if (numericAmount > 999999999.99) {
    throw new Error("amount exceeds EPC QR maximum");
  }

  const amountField = `EUR${numericAmount.toFixed(2)}`;

  // Structured (OGM) and unstructured remittance are mutually exclusive.
  const ogmDigits = structuredMededeling
    ? String(structuredMededeling).replace(/\D/g, "").slice(0, 25)
    : "";
  const useStructured = ogmDigits.length > 0;
  const structuredField = useStructured ? ogmDigits : "";
  const unstructuredField = useStructured
    ? ""
    : String(unstructuredRemittance ?? "")
        .trim()
        .slice(0, 140);

  // BCD / version 002 / UTF-8 / SCT
  const lines = [
    "BCD",
    "002",
    "1",
    "SCT",
    cleanBic,
    name,
    cleanIban,
    amountField,
    "", // purpose (optional)
    structuredField,
    unstructuredField,
  ];

  // Spec: last populated element is not followed by a trailing separator.
  while (lines.length > 0 && lines[lines.length - 1] === "") {
    lines.pop();
  }

  return lines.join("\n");
};

module.exports = {
  generateEpcQrPayload,
};
